/**
 * Reconcile the authenticated user's UI language with their stored account
 * preference — in BOTH directions.
 *
 * Pre-auth screens pick a UI language from localStorage / navigator and a GeoIP
 * upgrade (see useGeoLanguageDefault). Once the user logs in, two cases must be
 * told apart:
 *
 *   1. The user merely landed on a page showing the GeoIP/browser default and
 *      logged in without touching the language control. That default is a *guess*
 *      — the account `preferred_language` is the source of truth and must win, so
 *      a user whose account is English but who logs in from a Spanish-detected
 *      location ends up in English.
 *
 *   2. The user *explicitly* picked a language on the pre-auth screen (the
 *      LanguageSelector sets LANGUAGE_EXPLICIT_KEY). That is NOT a guess — it is a
 *      deliberate choice that should become the durable account preference, not be
 *      silently reverted by it. Without this, a user whose account is Spanish but
 *      who switches the login page to English every time never persists English
 *      (the login-screen selectors only write the cookie/localStorage, never the
 *      account) and "resets to Spanish on every login" forever.
 *
 * So: when an explicit choice differs from the stored preference, we adopt it INTO
 * the account (via updateUser) and consume the explicit flag — thereafter the
 * account is the single source of truth (a later change by an admin or another
 * device is not flipped back). When there is no explicit choice, the account
 * preference wins and is applied to the UI as before.
 *
 * `changeLanguage` / `persistKnownLanguage` cache the active choice to localStorage
 * and the cross-subdomain cookie so a reload — and the SSO-callback / 2FA host
 * (`app.panluma.ai`) — start in the right language; DirectionContext updates
 * RTL/LTR off the resulting languageChanged event. Mount once inside the auth tree
 * (see App.tsx).
 */
import { useEffect, useRef } from 'react';

import { useAuth } from '../shared/auth';
import type { SupportedLanguage } from '../shared/auth/types';
import { useEmulation } from '../shared/contexts/EmulationContext';
import i18n, { SUPPORTED_LANGUAGES, changeLanguage, persistKnownLanguage } from './config';
import { LANGUAGE_EXPLICIT_KEY } from './useGeoLanguageDefault';

function isSupported(lang: string | null | undefined): lang is SupportedLanguage {
  return !!lang && SUPPORTED_LANGUAGES.some((l) => l.code === lang);
}

export function useApplyUserLanguage(): void {
  const { isAuthenticated, user, updateUser } = useAuth();
  const { isEmulating } = useEmulation();
  const preferred = user?.preferred_language;
  // Guards a single in-flight account write so a re-render during the async
  // updateUser doesn't fire a second one.
  const adoptingRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !isSupported(preferred)) return;

    // While emulating a tenant user, `updateUser` would write the *emulated*
    // user's account — never adopt a language during emulation. The sysadmin's
    // own explicit flag was already reconciled on their own login, so this only
    // skips the account write; the account-preference branch below still applies
    // the emulated user's language to the UI.
    if (!isEmulating && typeof localStorage !== 'undefined' && localStorage.getItem(LANGUAGE_EXPLICIT_KEY)) {
      // The explicit choice is whatever the UI currently shows — the selector set
      // i18n.language and the flag together, and nothing downgrades an explicit
      // choice between then and here. Compare base codes ("en-US" -> "en").
      const chosen = i18n.language?.split('-')[0];
      if (isSupported(chosen) && chosen !== preferred) {
        if (!adoptingRef.current) {
          adoptingRef.current = true;
          // The UI already shows `chosen`; make it durable cross-subdomain and on
          // the account. Clear the explicit flag only after a successful save so
          // a failed write (e.g. offline) is retried on the next mount.
          persistKnownLanguage(chosen);
          // `chosen` is already narrowed to SupportedLanguage by isSupported above.
          void updateUser({ preferred_language: chosen })
            .then(() => {
              localStorage.removeItem(LANGUAGE_EXPLICIT_KEY);
            })
            .catch(() => {
              // Best-effort: the session still shows the user's explicit choice;
              // a failed save just means it won't follow them to another device.
            })
            .finally(() => {
              adoptingRef.current = false;
            });
        }
        // Don't fall through to the account-wins branch — that would switch the UI
        // back to the (stale) stored preference we're in the middle of replacing.
        return;
      }
      // Explicit flag but the choice already matches the account (or the UI lang
      // is unsupported): the account is consistent, so consume the flag. This is
      // what prevents a one-time explicit pick from sticky-flipping a later
      // account change back on every subsequent login.
      localStorage.removeItem(LANGUAGE_EXPLICIT_KEY);
    }

    // No fresh explicit choice — the account preference is the source of truth and
    // must win over any GeoIP/browser default. Record it in the cross-subdomain
    // cookie unconditionally (even when the active UI language already matches) so
    // the most-recently-signed-in user's language follows them to other
    // `*.panluma.ai` subdomains and the SSO-callback / 2FA host.
    persistKnownLanguage(preferred);
    if (i18n.language?.split('-')[0] === preferred) return;
    void changeLanguage(preferred);
  }, [isAuthenticated, preferred, isEmulating, updateUser]);
}

/**
 * Mount-once component that wires {@link useApplyUserLanguage} into the React
 * tree. Place inside the auth providers (see App.tsx) alongside the other
 * auth-reactive bootstraps.
 */
export function UserLanguageSync(): null {
  useApplyUserLanguage();
  return null;
}
